import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Separator } from '@/components/shadcn/separator'
import { cn } from '@/lib/utils'
import {
  MDXEditor,
  UndoRedo,
  BoldItalicUnderlineToggles,
  toolbarPlugin,
  listsPlugin,
  headingsPlugin,
  quotePlugin,
  // BlockTypeSelect,
  ListsToggle,
  linkDialogPlugin,
  linkPlugin,
  tablePlugin,
  CreateLink,
  InsertTable,
  MDXEditorMethods,
  HEADING_LEVEL,
  diffSourcePlugin,
  DiffSourceToggleWrapper,
  InsertCodeBlock,
  codeBlockPlugin,
  codeMirrorPlugin,
  CodeMirrorEditor,
  thematicBreakPlugin,
  InsertThematicBreak,
  frontmatterPlugin,
  InsertFrontmatter,
} from '@mdxeditor/editor'
import '@mdxeditor/editor/style.css'
import { forwardRef, useRef } from 'react'
import { Control, FieldPath, FieldValues } from 'react-hook-form'
import { CustomBlockTypeSelect } from './CustomBlockTypeSelect'

interface MDXEditorComponentProps {
  markdown: string
  onChange: (markdown: string) => void
  placeholder?: string
  className?: string
  contentEditableClassName?: string
  disabled?: boolean
  allowedHeadingLevels?: readonly HEADING_LEVEL[]
  showToolbar?: boolean
  overlayContainer?: HTMLDivElement
  autoFocus?: boolean
  diffMarkdown?: string
}

export const MDXEditorComponent = forwardRef<MDXEditorMethods, MDXEditorComponentProps>(
  (
    {
      markdown,
      onChange,
      placeholder = 'Start writing...',
      className,
      contentEditableClassName,
      disabled = false,
      // allowedHeadingLevels = [1, 2, 3] as const,
      showToolbar = true,
      overlayContainer,
      autoFocus = false,
      diffMarkdown,
    },
    ref
  ) => {
    const plugins = [
      // headingsPlugin({ allowedHeadingLevels }),
      headingsPlugin(),
      listsPlugin(),
      quotePlugin(),
      tablePlugin(),
      linkPlugin(),
      linkDialogPlugin(),
      thematicBreakPlugin(),
      frontmatterPlugin(),
      diffSourcePlugin({ viewMode: 'rich-text', diffMarkdown }),
      codeBlockPlugin({
        codeBlockEditorDescriptors: [{ priority: -10, match: (_) => true, Editor: CodeMirrorEditor }],
      }),
      codeMirrorPlugin({
        autoLoadLanguageSupport: true,
        codeBlockLanguages: {
          // Common languages
          json: 'JSON',
          py: 'Python',
          cs: 'C#',
          yaml: 'YAML',
          ts: 'TypeScript',
          tsx: 'TSX',
          mermaid: 'Mermaid',
          // typescript: 'TypeScript',
          // html: 'HTML',
          // css: 'CSS',
        },
      }),
      ...(showToolbar
        ? [
            toolbarPlugin({
              toolbarContents: () => (
                <div className="flex w-full flex-wrap-reverse items-center gap-2">
                  <DiffSourceToggleWrapper>
                    <UndoRedo />
                    <Separator orientation="vertical" className="h-7 bg-muted" />
                    <BoldItalicUnderlineToggles />
                    <Separator orientation="vertical" className="h-7 bg-muted" />
                    {/* <BlockTypeSelect /> */}
                    <CustomBlockTypeSelect />
                    <Separator orientation="vertical" className="h-7 bg-muted" />
                    <CreateLink />
                    <Separator orientation="vertical" className="h-7 bg-muted" />
                    <InsertTable />
                    <InsertCodeBlock />
                    <InsertThematicBreak />
                    <Separator orientation="vertical" className="h-7 bg-muted" />
                    <ListsToggle />
                    <Separator orientation="vertical" className="h-7 bg-muted" />
                    <InsertFrontmatter />
                  </DiffSourceToggleWrapper>
                </div>
              ),
            }),
          ]
        : []),
    ]

    return (
      <MDXEditor
        ref={ref}
        markdown={markdown}
        onChange={onChange}
        placeholder={placeholder}
        className={cn('[&_.cm-editor]:h-96', className)}
        contentEditableClassName={cn(
          'h-96 bg-background prose prose-sm max-w-none overflow-y-auto dark:prose-invert dark:prose-p:text-foreground dark:prose-li:text-foreground',
          contentEditableClassName
        )}
        plugins={plugins}
        readOnly={disabled}
        overlayContainer={overlayContainer}
        autoFocus={autoFocus}
      />
    )
  }
)

MDXEditorComponent.displayName = 'MDXEditorComponent'

// Form field wrapper component for easier integration with React Hook Form
interface MDXEditorFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>
  name: TName
  label: string
  placeholder?: string
  className?: string
  contentEditableClassName?: string
  disabled?: boolean
  allowedHeadingLevels?: readonly HEADING_LEVEL[]
  showToolbar?: boolean
  autoFocus?: boolean
  diffMarkdown?: string
}

export function MDXEditorField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  placeholder,
  className,
  contentEditableClassName,
  disabled,
  allowedHeadingLevels,
  showToolbar,
  autoFocus,
  diffMarkdown,
}: MDXEditorFieldProps<TFieldValues, TName>) {
  const overlayContainerRef = useRef<HTMLDivElement>(null)
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <div ref={overlayContainerRef} className="relative w-full">
              <MDXEditorComponent
                markdown={field.value || ''}
                onChange={field.onChange}
                placeholder={placeholder}
                contentEditableClassName={contentEditableClassName}
                className={cn('[&_.cm-editor]:min-h-96', className)}
                disabled={disabled}
                allowedHeadingLevels={allowedHeadingLevels}
                showToolbar={showToolbar}
                overlayContainer={overlayContainerRef.current ?? undefined}
                autoFocus={autoFocus}
                diffMarkdown={diffMarkdown}
              />
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
